import { useEffect, useRef, useState } from "react";
import { Box, LoaderCircle, MessageSquarePlus, Plus, RotateCcw, X } from "lucide-react";
import {
  elementKind,
  type SceneBlock,
  type SceneElement,
  type SceneRoof,
  type SceneSpec,
  type SceneTree,
  type SceneWall,
} from "../shared/scene-spec";
import type { ScenePick, SceneView } from "./scene-engine";

const materialLabel = (m: string) => m[0].toUpperCase() + m.slice(1);
const metres = (n: number) => `${Math.round(n * 100) / 100} m`;

/** One line about a picked element, in the user's words. */
export function describeElement(e: SceneElement) {
  const kind = elementKind(e);
  if (kind === "wall") {
    const w = e as SceneWall;
    const doors = w.openings.filter((o) => o.type === "door").length,
      windows = w.openings.length - doors;
    const openings = [
      doors && `${doors} door${doors > 1 ? "s" : ""}`,
      windows && `${windows} window${windows > 1 ? "s" : ""}`,
    ].filter(Boolean);
    return `Wall · ${metres(w.height)} high · ${metres(w.thickness)} thick${openings.length ? ` · ${openings.join(", ")}` : ""} · ${materialLabel(w.material)}`;
  }
  if (kind === "roof") {
    const r = e as SceneRoof;
    const style =
      r.style === "flat"
        ? "Flat roof"
        : `${r.style === "gable" ? "Gable" : "Mono-pitch"} roof · ${r.pitch}°`;
    return `${style} · eaves at ${metres(r.base)} · ${metres(r.overhang)} overhang`;
  }
  if (kind === "tree") {
    const t = e as SceneTree;
    return `Tree · ${metres(t.height)} high · ${metres(t.spread * 2)} canopy`;
  }
  const b = e as SceneBlock;
  return `${metres(b.height)} high${b.base ? ` · from ${metres(b.base)}` : ""} · ${materialLabel(b.material)}`;
}

/**
 * The agent's 3D view: a declarative scene raised from the plan. Clicking an
 * element shows what it is; Comment on plan returns to the drawing at the
 * point clicked. Add to board places it as an ordinary 3D model.
 */
export function SceneViewer({
  scene,
  close,
  addToBoard,
  commentOnPlan,
}: {
  scene: SceneSpec;
  close: () => void;
  addToBoard?: (scene: SceneSpec) => Promise<void>;
  commentOnPlan?: (scene: SceneSpec, pick: ScenePick) => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const controller = useRef<{ setView: (v: SceneView) => void; replay: () => void; dispose: () => void }>(undefined);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [pick, setPick] = useState<ScenePick | null>(null);
  const [adding, setAdding] = useState<"idle" | "busy" | "done">("idle");

  useEffect(() => {
    let disposed = false;
    const reducedMotion =
      typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    import("./scene-engine")
      .then(({ mountScene }) => {
        if (disposed || !host.current) return;
        controller.current = mountScene(host.current, scene, { onPick: setPick, reducedMotion });
        setLoading(false);
      })
      .catch((e: Error) => {
        if (!disposed) {
          setError(/webgl/i.test(e.message) ? "This browser could not start 3D graphics." : e.message);
          setLoading(false);
        }
      });
    return () => {
      disposed = true;
      controller.current?.dispose();
      controller.current = undefined;
    };
  }, [scene]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  async function add() {
    if (!addToBoard || adding === "busy") return;
    setAdding("busy");
    setError("");
    try {
      await addToBoard(scene);
      setAdding("done");
    } catch (e) {
      setError((e as Error).message);
      setAdding("idle");
    }
  }

  const scale = scene.approximate
    ? scene.calibrated
      ? "Traced from the drawing, to scale · approximate"
      : "Traced from the drawing · approximate"
    : scene.calibrated
      ? "To scale, from the sheet"
      : scene.plan
        ? "Scale assumed: the sheet is not calibrated"
        : "Dimensions in metres";

  return (
    <div className="model-viewer-backdrop" onPointerDown={close}>
      <section
        className="model-viewer scene-viewer"
        role="dialog"
        aria-modal="true"
        aria-label={`3D view of ${scene.title}`}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="model-viewer-format">
              <Box size={15} /> 3D model · {scale}
            </span>
            <strong title={scene.title}>{scene.title}</strong>
          </div>
          <div className="model-viewer-header-actions">
            {addToBoard && (
              <button onClick={() => void add()} disabled={adding !== "idle"}>
                {adding === "busy" ? <LoaderCircle className="spin" size={16} /> : <Plus size={16} />}
                {adding === "done" ? "Added to board" : "Add to board"}
              </button>
            )}
            <button aria-label="Close 3D view" onClick={close}>
              <X size={20} />
            </button>
          </div>
        </header>
        <div className="model-viewer-stage">
          <div ref={host} className="model-viewer-canvas" />
          {loading && (
            <div className="model-viewer-state" role="status">
              <LoaderCircle className="spin" size={24} /> Preparing the model…
            </div>
          )}
          {error && (
            <div className="model-viewer-state model-viewer-error" role="alert">
              <Box size={28} />
              <strong>3D view unavailable</strong>
              <span>{error}</span>
            </div>
          )}
          {pick && (
            <aside className="scene-pick" aria-live="polite">
              <strong>{pick.element.name}</strong>
              <span>{describeElement(pick.element)}</span>
              {commentOnPlan && scene.plan && (
                <button onClick={() => commentOnPlan(scene, pick)}>
                  <MessageSquarePlus size={15} /> Comment on plan
                </button>
              )}
            </aside>
          )}
        </div>
        <footer>
          <div className="model-view-buttons" aria-label="Views">
            <button onClick={() => controller.current?.setView("iso")}>3/4</button>
            <button onClick={() => controller.current?.setView("front")}>Front</button>
            <button onClick={() => controller.current?.setView("top")}>Top</button>
            <button onClick={() => controller.current?.setView("eye")}>Eye level</button>
            <button onClick={() => controller.current?.replay()} aria-label="Replay the build" title="Replay the build">
              <RotateCcw size={15} />
            </button>
          </div>
          <span>{pick ? "" : "Drag to orbit · scroll to zoom · click a part to name it"}</span>
        </footer>
      </section>
    </div>
  );
}
